'use client';

import type { ArchitectureState } from '@/lib/architecture-state';

// Rough daily expected throughput per source. Not load-bearing — purely a
// visual normalization so the fill bar reads as "% of a typical day."
const EXPECTED_DAILY_BY_SOURCE: Record<string, number> = {
  sapi: 600_000,
  fmp: 250_000,
  sec: 1_100,
  computed: 1, // anything > 0 → 100%
};

const UNIT_BY_SOURCE: Record<string, string> = {
  sapi: 'cells',
  fmp: 'calls',
  sec: 'queries',
  computed: 'rows',
};

const STATUS_DOT: Record<string, string> = {
  ok: 'bg-emerald-400 shadow-[0_0_6px_rgba(74,222,128,0.7)]',
  partial: 'bg-amber-300 shadow-[0_0_6px_rgba(252,211,77,0.7)]',
  fail: 'bg-red-400 shadow-[0_0_6px_rgba(248,113,113,0.7)]',
};

function fmtCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

export function SourcePool({ source }: { source: ArchitectureState['sources'][number] }): React.JSX.Element {
  const expected = EXPECTED_DAILY_BY_SOURCE[source.id] ?? 1;
  const fillPct = Math.max(0, Math.min(1, source.filled / expected));
  const unit = UNIT_BY_SOURCE[source.id] ?? 'rows';

  return (
    <div className="rounded border border-zinc-800 bg-zinc-900/80 px-2 py-1.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[source.status] ?? STATUS_DOT.ok}`} aria-hidden />
          <span className="text-[10px] font-semibold tracking-wide text-white/90">{source.label}</span>
        </div>
        <span className="text-[9px] tabular-nums text-white/60">
          {fmtCount(source.filled)} <span className="text-white/40">{unit}</span>
        </span>
      </div>
      <div className="mt-1 h-1 rounded-full bg-zinc-800 overflow-hidden">
        <div
          className="h-full bg-[#c9a84c] transition-[width] duration-500 ease-out"
          style={{ width: `${(fillPct * 100).toFixed(1)}%` }}
        />
      </div>
    </div>
  );
}
